import { endOfMonth, subDays } from 'date-fns'

import { Contract } from '@enechain/eposition-proto/bffcommon/v1/eex_model_pb'
import {
  AreaCode,
  AreaType,
} from '@enechain/eposition-proto/bfftypes/v1/area_pb'
import {
  EEXHourType,
  EEXPeriodType,
} from '@enechain/eposition-proto/bfftypes/v1/eex_pb'
import {
  DeliveryHourType,
  DeliveryPeriodType,
} from '@enechain/eposition-proto/types/v1/deal_pb'
import { formatTimeStampToJSTYYYYMMddDate } from '@enechain/eposition-utils'

export type EEXContractModel = {
  label: string
  value: {
    deliveryStartDate: Date
    deliveryEndDate: Date
    code: string
    instrument: string
  }
}

type Props = {
  value: Contract[]
  useEslLabel: boolean
  now?: Date
}
export function convertEEXContractModelList({
  value,
  useEslLabel,
  now,
}: Props): EEXContractModel[] {
  const currentMonthEnd = endOfMonth(now ?? new Date())

  return value
    .filter(
      (contract) =>
        contract.deliveryStartAt !== undefined &&
        contract.deliveryEndAt !== undefined,
    )
    .filter((contract) => {
      if (contract.deliveryStartAt === undefined) {
        return false
      }
      const deliveryStartDate = formatTimeStampToJSTYYYYMMddDate(
        contract.deliveryStartAt,
      )

      return deliveryStartDate > currentMonthEnd
    })
    .map((contract) => {
      if (
        contract.deliveryStartAt === undefined ||
        contract.deliveryEndAt === undefined
      ) {
        throw new Error('Contract is missing delivery dates')
      }
      return {
        label: useEslLabel
          ? contract.instrumentDisplayName
          : contract.touchpointDisplayName,
        value: {
          deliveryStartDate: formatTimeStampToJSTYYYYMMddDate(
            contract.deliveryStartAt,
          ),
          deliveryEndDate: subDays(
            formatTimeStampToJSTYYYYMMddDate(contract.deliveryEndAt),
            1,
          ), // delivery end atは翌日の0時なので、1日前にする
          code: contract.code,
          instrument: contract.instrument,
        },
      }
    })
}

export function filterEEXContractByAreaCode({
  eexContracts,
  areaCode,
}: {
  eexContracts: Contract[]
  areaCode: AreaCode
}): Contract[] {
  return eexContracts.filter((contract) => {
    if (areaCode === AreaCode.UNSPECIFIED) {
      return true
    }
    return (
      (contract.areaType === AreaType.TOKYO && areaCode === AreaCode.TOKYO) ||
      (contract.areaType === AreaType.KANSAI && areaCode === AreaCode.KANSAI) ||
      (contract.areaType === AreaType.CHUBU && areaCode === AreaCode.CHUBU)
    )
  })
}

export function filterEEXContractByDeliveryPeriodType({
  eexContracts,
  deliveryPeriodType,
}: {
  eexContracts: Contract[]
  deliveryPeriodType: DeliveryPeriodType
}): Contract[] {
  return eexContracts.filter((contract) => {
    if (deliveryPeriodType === DeliveryPeriodType.UNSPECIFIED) {
      return true
    }
    return (
      (contract.periodType === EEXPeriodType.EEX_PERIOD_TYPE_MONTH &&
        deliveryPeriodType === DeliveryPeriodType.MONTH) ||
      (contract.periodType === EEXPeriodType.EEX_PERIOD_TYPE_QUARTER &&
        deliveryPeriodType === DeliveryPeriodType.QUARTER) ||
      (contract.periodType === EEXPeriodType.EEX_PERIOD_TYPE_SEASON &&
        deliveryPeriodType === DeliveryPeriodType.SEASON) ||
      (contract.periodType === EEXPeriodType.EEX_PERIOD_TYPE_YEAR &&
        deliveryPeriodType === DeliveryPeriodType.FISCAL_YEAR &&
        contract.instrument.startsWith('FY'))
    )
  })
}

export function filterEEXContractByDeliveryHourType({
  eexContracts,
  deliveryHourType,
}: {
  eexContracts: Contract[]
  deliveryHourType: DeliveryHourType
}): Contract[] {
  return eexContracts.filter((contract) => {
    if (deliveryHourType === DeliveryHourType.UNSPECIFIED) {
      return true
    }
    return (
      (contract.hourType === EEXHourType.EEX_HOUR_TYPE_BASE &&
        deliveryHourType === DeliveryHourType.BASE) ||
      (contract.hourType === EEXHourType.EEX_HOUR_TYPE_PEAK &&
        deliveryHourType === DeliveryHourType.PEAK)
    )
  })
}

// contractListから絞り込んでproductCode取得することも考えたが、実装のシンプルさを優先した。
// 後々、contractListを引き回してeexContractのproductCodeを取得することに対する"めんどうさ"以外の異論はないです。
export type EEXProductCode = 'TABF' | 'TAPF' | 'KABF' | 'KAPF' | 'FMB' | 'FMP'

export function getEEXProductCode(
  areaCode: AreaCode,
  deliveryHourType: DeliveryHourType,
): EEXProductCode | undefined {
  switch (areaCode) {
    case AreaCode.TOKYO:
      switch (deliveryHourType) {
        case DeliveryHourType.BASE:
          return 'TABF'
        case DeliveryHourType.PEAK:
          return 'TAPF'
        default:
          return undefined
      }
    case AreaCode.KANSAI:
      switch (deliveryHourType) {
        case DeliveryHourType.BASE:
          return 'KABF'
        case DeliveryHourType.PEAK:
          return 'KAPF'
        default:
          return undefined
      }
    case AreaCode.CHUBU:
      switch (deliveryHourType) {
        case DeliveryHourType.BASE:
          return 'FMB'
        case DeliveryHourType.PEAK:
          return 'FMP'
        default:
          return undefined
      }
    default:
      return undefined
  }
}
